import { useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { ArrowRight, Shield, Clock, BarChart3, Globe, AlertCircle, Mail, MessageSquare, FileText, CheckCircle, XCircle, HelpCircle } from 'lucide-react'
import Breadcrumb from '../components/Breadcrumb'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const countries = [
  { name: 'United States', code: 'US', flag: '🇺🇸' },
  { name: 'Saudi Arabia', code: 'SA', flag: '🇸🇦' },
  { name: 'UAE', code: 'AE', flag: '🇦🇪' },
  { name: 'Pakistan', code: 'PK', flag: '🇵🇰' },
]

const policySections = [
  {
    id: 'general',
    title: 'General Non-Refundable Policy',
    icon: XCircle,
    content: `As a general rule, fees paid for digital products, SaaS access, AI usage, software services, consultancy, and delivered work are non-refundable once the service is provided, consumed, activated, or work has started.

This includes:
• Digital products & SaaS access
• AI & software services
• Consultancy & delivered work`
  },
  {
    id: 'eligibility',
    title: 'Refund Eligibility Window',
    icon: Clock,
    content: `A refund may be reviewed only when the request is submitted within 3 days of initial purchase and the service usage remains below the stated usage threshold, such as fewer than 20 messages for qualifying AI/SaaS services.

• 3-day review window
• Usage below threshold
• Qualifying AI/SaaS services`
  },
  {
    id: 'consumed',
    title: 'Consumed Services',
    icon: BarChart3,
    content: `Services are considered consumed when usage, access, deployment, development, consultancy, API consumption, server allocation, AI model processing, campaign execution, or resource reservation has occurred.

• Usage & access
• Deployment & development
• API & resource consumption`
  },
  {
    id: 'professional',
    title: 'Professional Service Work',
    icon: FileText,
    content: `Custom development, design, integration, DevOps, implementation, telecom configuration, AI training, discovery, architecture, documentation, and consulting fees are generally non-refundable after work begins.

• Custom development & design
• DevOps & implementation
• Consulting & AI training`
  },
  {
    id: 'technical',
    title: 'Technical Issue Review',
    icon: Shield,
    content: `If a significant and persistent technical defect is verifiably caused by Technifire systems, the customer may request review by sharing screenshots, logs, timestamps, account details, and error evidence.

• Verified platform-side defects
• Evidence submission required
• Case-by-case review`
  },
  {
    id: 'chargebacks',
    title: 'Chargebacks',
    icon: AlertCircle,
    content: `Customers should contact support before filing a bank dispute. Chargebacks without prior support review may result in service suspension, account review, and submission of usage records to the payment provider.

• Contact support first
• Service suspension risk
• Usage records submitted`
  }
]

const countryGuidance = [
  { country: 'United States', flag: '🇺🇸', text: 'For USA customers, Technifire provides digital and professional services that are generally non-refundable once accessed, consumed, delivered, or started. Refund review is limited to the 3-day eligibility window, low usage, duplicate billing, or verified platform-side technical defects.' },
  { country: 'Saudi Arabia', flag: '🇸🇦', text: 'For Saudi Arabia customers, the 3-day refund window acts as a fair review period for qualifying digital services. Once the service is consumed, delivered, configured, or usage exceeds the threshold, fees are treated as non-refundable.' },
  { country: 'UAE', flag: '🇦🇪', text: 'For UAE customers, the 3-day refund window provides a customer satisfaction review for qualifying digital services. Refunds remain subject to usage, service delivery status, project work completed, and account records.' },
  { country: 'Pakistan', flag: '🇵🇰', text: 'For Pakistan customers, Technifire offers the 3-day refund window as a customer satisfaction review for qualifying digital services. Refunds remain subject to usage, service delivery status, project work completed, and account records.' },
]

const nonRefundableSituations = [
  { title: 'Usage Exceeded', desc: 'Usage crosses the defined limit, including 20 or more messages for qualifying AI/SaaS services.' },
  { title: 'Late Request', desc: 'The refund request is submitted after 3 days from the initial purchase date.' },
  { title: 'Work Started', desc: 'Custom software, design, integration, DevOps, consulting, or AI implementation work has started.' },
  { title: 'Resources Consumed', desc: 'Cloud, AI model, API, server, or third-party costs have been consumed or reserved.' },
  { title: 'Customer-Side Issues', desc: 'Issues caused by device, internet, third-party accounts, access, credentials, or external systems.' },
  { title: 'Change of Mind', desc: 'Change of mind after activation, usage, setup, delivery, or project work is not eligible for refund.' },
]

const faqs = [
  { q: 'Are all Technifire services refundable?', a: 'No. Digital services, SaaS access, AI usage, professional services, implementation, and delivered project work are generally non-refundable once provided, consumed, configured, accessed, or started.' },
  { q: 'What is the standard refund window?', a: 'Refund review may be available within 3 days of initial purchase where usage remains below the defined threshold and the service has not been substantially consumed or delivered.' },
  { q: 'Can custom development work be refunded?', a: 'Custom software development, architecture, UI/UX, DevOps, AI implementation, and consulting fees are generally non-refundable after work begins.' },
  { q: 'What if there is a technical problem?', a: 'If the issue is a significant, persistent, verifiable platform-side defect caused by Technifire systems, support may review evidence and determine an appropriate resolution or refund eligibility.' },
  { q: 'What happens if I file a chargeback?', a: 'Technifire may suspend access, review the account, and provide service delivery records, policy terms, and usage data to the payment provider or financial institution.' },
]

export default function RefundPolicy() {
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const elements = sectionRef.current?.querySelectorAll('.refund-animate')
      if (elements && elements.length > 0) {
        gsap.fromTo(elements, { y: 40, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top bottom', toggleActions: 'play none none none', invalidateOnRefresh: true }
        })
      }
    }, sectionRef)
    return () => ctx.revert()
  }, [])

  return (
    <div className="min-h-screen bg-[#050505] pb-20" ref={sectionRef}>
      <Helmet>
        <title>Refund Policy — Technifire Software</title>
        <meta name="description" content="Technifire Software Refund Policy — terms regarding refunds for digital products, SaaS access, AI services, consultancy, and software development." />
        <link rel="canonical" href="https://technifire.io/refund-policy" />
        <meta property="og:title" content="Refund Policy — Technifire Software" />
        <meta property="og:description" content="Refund policy for Technifire Software digital products, SaaS, AI, and development services." />
        <meta property="og:url" content="https://technifire.io/refund-policy" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Breadcrumb current="REFUND POLICY" />

      {/* Hero */}
      <div className="px-6 lg:px-10 pt-8 pb-12">
        <div className="max-w-4xl refund-animate">
          <div className="flex flex-wrap gap-2 mb-4">
            {countries.map(c => (
              <span key={c.code} className="text-mono text-[#9A9A9A] bg-white/5 px-3 py-1 rounded-full">{c.flag} {c.code}</span>
            ))}
          </div>
          <span className="text-mono text-[#9A9A9A] mb-3 block">REFUND POLICY</span>
          <h1 className="text-h2 uppercase text-white mb-6">
            REFUND POLICY FOR<br />DIGITAL SERVICES
          </h1>
          <p className="text-lg text-[#9A9A9A] leading-relaxed max-w-2xl">
            This page explains how refund requests are reviewed for Technifire digital products, AI services, software development, SaaS subscriptions, and professional service engagements across the USA, Saudi Arabia, UAE, and Pakistan.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 text-sm text-[#9A9A9A]">
            <span>Last Updated: July 16, 2026</span>
            <span className="text-white/30 mx-2">|</span>
            <a href="mailto:info@technifire.io" className="text-white hover:text-[#9A9A9A] transition-colors">Contact Support</a>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="px-6 lg:px-10 pb-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 refund-animate">
          {[
            { value: '3 Days', label: 'Standard eligibility window', icon: Clock },
            { value: '<20', label: 'Usage threshold for eligible refunds', icon: BarChart3 },
            { value: '4', label: 'Regional policy sections', icon: Globe },
            { value: 'Case', label: 'Technical issue refunds reviewed', icon: Shield },
          ].map((s, i) => (
            <div key={i} className="bg-[#0a0a0a] rounded-xl p-5 border border-white/5">
              <s.icon className="w-5 h-5 text-white/40 mb-3" />
              <span className="text-2xl font-light text-white block">{s.value}</span>
              <p className="text-mono text-[#9A9A9A] mt-1 text-[10px]">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Policy Sections Grid */}
      <div className="px-6 lg:px-10 pb-16">
        <div className="max-w-6xl mx-auto">
          <span className="text-mono text-[#9A9A9A] mb-6 block refund-animate">POLICY OVERVIEW</span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {policySections.map((section, i) => (
              <div key={section.id} className="refund-animate bg-[#0a0a0a] rounded-2xl p-6 border border-white/5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center shrink-0 mt-1">
                    <section.icon className="w-5 h-5 text-[#9A9A9A]" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-2">{section.title}</h3>
                    <div className="text-sm text-[#9A9A9A] leading-relaxed whitespace-pre-line">{section.content}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Country-Specific Guidance */}
      <div className="px-6 lg:px-10 pb-16">
        <div className="max-w-6xl mx-auto">
          <span className="text-mono text-[#9A9A9A] mb-6 block refund-animate">COUNTRY-SPECIFIC GUIDANCE</span>
          <div className="space-y-4">
            {countryGuidance.map((c, i) => (
              <div key={i} className="refund-animate bg-[#0a0a0a] rounded-2xl p-6 border border-white/5">
                <div className="flex items-start gap-4">
                  <span className="text-2xl shrink-0 mt-0.5">{c.flag}</span>
                  <div>
                    <h3 className="text-white font-medium mb-1">{c.country}</h3>
                    <p className="text-sm text-[#9A9A9A] leading-relaxed">{c.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Non-Refundable Situations */}
      <div className="px-6 lg:px-10 pb-16">
        <div className="max-w-6xl mx-auto">
          <span className="text-mono text-[#9A9A9A] mb-6 block refund-animate">NON-REFUNDABLE SITUATIONS</span>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {nonRefundableSituations.map((s, i) => (
              <div key={i} className="refund-animate bg-[#0a0a0a] rounded-2xl p-6 border border-white/5">
                <XCircle className="w-5 h-5 text-[#B7000E] mb-3" />
                <h3 className="text-white font-medium mb-2 text-sm">{s.title}</h3>
                <p className="text-sm text-[#9A9A9A] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Refund Process */}
      <div className="px-6 lg:px-10 pb-16">
        <div className="max-w-6xl mx-auto">
          <span className="text-mono text-[#9A9A9A] mb-6 block refund-animate">REFUND REQUEST PROCESS</span>
          <div className="bg-[#0a0a0a] rounded-2xl p-8 border border-white/5 refund-animate">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: '01', icon: Mail, title: 'Email Support', desc: 'Send your request to info@technifire.io from your account email.' },
                { step: '02', icon: FileText, title: 'Share Details', desc: 'Include your name, company, invoice number, purchase date, and reason.' },
                { step: '03', icon: MessageSquare, title: 'Provide Evidence', desc: 'For technical issues, attach screenshots, logs, or error evidence.' },
                { step: '04', icon: CheckCircle, title: 'Review & Decision', desc: 'We review usage, delivery records, and payment details before responding.' },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                    <s.icon className="w-5 h-5 text-[#9A9A9A]" />
                  </div>
                  <span className="text-mono text-[#9A9A9A] text-[10px]">{s.step}</span>
                  <h3 className="text-white text-sm font-medium mt-1 mb-2">{s.title}</h3>
                  <p className="text-xs text-[#9A9A9A] leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-[#9A9A9A] mt-8 pt-6 border-t border-white/10">
              Approved refunds may take several business days to appear depending on your bank, card network, and payment provider. Processing fees, third-party fees, or consumed credits may be deducted where applicable.
            </p>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="px-6 lg:px-10 pb-16">
        <div className="max-w-6xl mx-auto">
          <span className="text-mono text-[#9A9A9A] mb-6 block refund-animate">FAQ</span>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="refund-animate bg-[#0a0a0a] rounded-2xl p-6 border border-white/5">
                <div className="flex items-start gap-4">
                  <HelpCircle className="w-5 h-5 text-[#9A9A9A] shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white text-sm font-medium mb-2">{faq.q}</h3>
                    <p className="text-sm text-[#9A9A9A] leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="px-6 lg:px-10 py-8 refund-animate">
        <div className="bg-gradient-to-r from-[#111] to-[#0a0a0a] rounded-2xl p-8 lg:p-12 border border-white/5 max-w-6xl mx-auto text-center">
          <h2 className="text-h3 text-white mb-4">Need Support?</h2>
          <p className="text-[#9A9A9A] mb-8">Submit your refund request for review. Include your account email, invoice details, service name, and reason for request.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacts" className="btn-primary inline-flex justify-center">
              <Mail className="w-4 h-4" />
              EMAIL SUPPORT
            </Link>
            <Link to="/contacts" className="btn-outline inline-flex justify-center">
              VISIT WEBSITE
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}